"use client"

import { useNavigate } from "react-router-dom"
import { useAuthContext } from "@/lib/auth-context"
import { CommunityAvatar } from "./community-avatar"
import { AuthorHoverCard } from "./author-hover-card"
import { analytics } from "@/src/utils/analytics"

interface AuthorLinkProps {
  authorId: string
  avatar: string
  displayName: string
  bio?: string
  size?: "sm" | "md"
  className?: string
}

const SIZE_MAP = {
  sm: { avatar: "w-5 h-5", text: "text-[13px] font-semibold", maxW: "max-w-[120px]" },
  md: { avatar: "w-10 h-10", text: "text-sm font-medium", maxW: "" },
} as const

export function AuthorLink({ authorId, avatar, displayName, bio, size = "sm", className }: AuthorLinkProps) {
  const navigate = useNavigate()
  const { currentUser } = useAuthContext()
  const s = SIZE_MAP[size]

  const isSelf = currentUser && authorId === currentUser.userId

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    analytics.track("CommunityViewUser", { author_id: authorId, display_name: displayName })
    if (isSelf) {
      navigate("/community/user/u-self")
    } else {
      window.open(`${import.meta.env.BASE_URL}community/user/${authorId}`, "_blank")
    }
  }

  return (
    <AuthorHoverCard authorId={authorId}>
      <button
        type="button"
        onClick={handleClick}
        className={`flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity ${className ?? ""}`}
      >
        <CommunityAvatar src={avatar} name={displayName} sizeClass={s.avatar} />
        <div className="flex flex-col items-start min-w-0">
          <span className={`${s.text} ${size === 'md' ? 'text-foreground' : 'text-foreground/90'} hover:text-foreground truncate ${s.maxW} transition-colors leading-tight`}>
            {displayName}
          </span>
          {bio !== undefined && (
            <span className="text-[10px] text-muted-foreground/40 truncate max-w-[180px] leading-tight">
              {bio || ""}
            </span>
          )}
        </div>
      </button>
    </AuthorHoverCard>
  )
}
